import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Select,
  MenuItem,
  List,
  ListItem,
  TextField,
  IconButton,
  Typography,
  Paper,
  Button,
  useTheme,
} from '@mui/material';
import { tokens } from '../../theme';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import ImageSearchIcon from '@mui/icons-material/ImageSearch';
import SendIcon from '@mui/icons-material/Send';

// src/scenes/chat/ChatPage.jsx
export default function ChatPage() {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  // Agents dropdown
  const [agents] = useState(['Agent Alpha', 'Agent Beta', 'Agent Gamma']);
  const [currentAgent, setCurrentAgent] = useState(agents[0]);

  // Sessions (previous chats)
  const [sessions, setSessions] = useState([
    { id: '1', name: 'Session 1' },
    { id: '2', name: 'Session 2' },
  ]);
  const [currentSession, setCurrentSession] = useState(sessions[0].id);

  // Messages in sessions
  const [messages, setMessages] = useState({
    '1': [],
    '2': [],
  });

  const [input, setInput] = useState('');
  const fileInputRef = useRef();

  // Auto-scroll on new messages
  useEffect(() => {
    const container = document.getElementById('message-container');
    if (container) container.scrollTop = container.scrollHeight;
  }, [messages, currentSession]);

  // Send text message
  const handleSend = () => {
    if (!input.trim()) return;
    const newMsg = { sender: 'user', type: 'text', content: input, timestamp: Date.now() };
    setMessages(prev => ({
      ...prev,
      [currentSession]: [...prev[currentSession], newMsg]
    }));
    setInput('');
  };

  // Upload image
  const handleFileChange = e => {
    const file = e.target.files[0];
    if (!file) return;
    const url = URL.createObjectURL(file);
    const newMsg = { sender: 'user', type: 'image', src: url, timestamp: Date.now() };
    setMessages(prev => ({
      ...prev,
      [currentSession]: [...prev[currentSession], newMsg]
    }));
    fileInputRef.current.value = '';
  };

  // Generate placeholder image
  const handleGenerateImage = () => {
    const prompt = window.prompt('Describe the image to generate:');
    if (!prompt) return;
    const placeholder = `https://via.placeholder.com/300x200?text=${encodeURIComponent(prompt)}`;
    const newMsg = { sender: 'agent', type: 'image', src: placeholder, timestamp: Date.now() };
    setMessages(prev => ({
      ...prev,
      [currentSession]: [...prev[currentSession], newMsg]
    }));
  };

  const sessionMessages = messages[currentSession] || [];

  return (
    <Box display="flex" height="100vh">
      {/* Sidebar: Sessions List */}
      <Box width={240} bgcolor={theme.palette.background.paper} p={2} sx={{ borderRight: 1, borderColor: 'divider' }}>
        <Typography variant="h6" color="text.primary" gutterBottom>
          Chats
        </Typography>
        <List>
          {sessions.map(sess => (
            <ListItem
              key={sess.id}
              button
              selected={sess.id === currentSession}
              onClick={() => setCurrentSession(sess.id)}
              sx={{
                color: sess.id === currentSession ? theme.palette.primary.main : 'text.primary'
              }}
            >
              {sess.name}
            </ListItem>
          ))}
        </List>
        <Button
          fullWidth
          variant="contained"
          color="primary"
          sx={{ mt: 2 }}
          onClick={() => {
            const name = window.prompt('New session name:');
            if (!name) return;
            const id = Date.now().toString();
            setSessions(prev => [...prev, { id, name }]);
            setMessages(prev => ({ ...prev, [id]: [] }));
            setCurrentSession(id);
          }}
        >
          New Chat
        </Button>
      </Box>

      {/* Main Chat Area */}
      <Box flexGrow={1} display="flex" flexDirection="column" bgcolor={theme.palette.background.default}>
        {/* Header: Agent Selector */}
        <Box p={2} display="flex" alignItems="center" gap={2} sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Typography variant="h6" color="text.primary">
            Agent:
          </Typography>
          <Select
            value={currentAgent}
            onChange={e => setCurrentAgent(e.target.value)}
            sx={{ color: 'text.primary', borderColor: 'divider' }}
          >
            {agents.map(agent => (
              <MenuItem key={agent} value={agent}>{agent}</MenuItem>
            ))}
          </Select>
        </Box>

        {/* Messages Display */}
        <Box
          id="message-container"
          flexGrow={1}
          p={2}
          overflow="auto"
        >
          {sessionMessages.map((msg, idx) => (
            <Box
              key={idx}
              display="flex"
              justifyContent={msg.sender === 'user' ? 'flex-end' : 'flex-start'}
              mb={2}
            >
              <Paper
                elevation={1}
                sx={{ p:2, maxWidth:'60%', borderRadius:2, backgroundColor: theme.palette.background.paper }}
              >
                {msg.type === 'text' ? (
                  <Typography sx={{ whiteSpace: 'pre-wrap', color: 'text.primary' }}>{msg.content}</Typography>
                ) : (
                  <Box component="img" src={msg.src} alt="chat-img" sx={{ width: '100%', borderRadius:1 }} />
                )}
                <Typography variant="caption" display="block" align={msg.sender === 'user' ? 'right' : 'left'} sx={{ mt:1, color: 'text.secondary' }}>
                  {new Date(msg.timestamp).toLocaleTimeString()}
                </Typography>
              </Paper>
            </Box>
          ))}
        </Box>

        {/* Input Area (fixed footer) */}
        <Box
          component="footer"
          sx={{
            position: 'fixed',
            bottom: 0,
            left: 240 + 320,  // adjust to match global sidebar + sessions width
            right: 0,
            zIndex: 1100,
            p:2,
            display:'flex',
            alignItems:'flex-end',
            gap:1,
            backgroundColor: 'background.paper',
            borderTop: '1px solid',
            borderColor: 'divider'
          }}
        >
          <IconButton onClick={() => fileInputRef.current.click()}>
            <AddPhotoAlternateIcon />
          </IconButton>
          <input
            type="file"
            ref={fileInputRef}
            style={{ display: 'none' }}
            accept="image/*"
            onChange={handleFileChange}
          />
          <IconButton onClick={handleGenerateImage}>
            <ImageSearchIcon />
          </IconButton>

          <TextField
            fullWidth
            multiline
            rows={3}
            maxRows={6}
            variant="outlined"
            placeholder="Type a message…"
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && !e.shiftKey && (e.preventDefault(), handleSend())}
            sx={{ backgroundColor: theme.palette.background.paper }}
          />

          <IconButton color="primary" onClick={handleSend}>
            <SendIcon />
          </IconButton>
        </Box>
      </Box>
    </Box>
  );
}